import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { defaultResource, resourceFromAttributes } from '@opentelemetry/resources';
import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { environment } from '@env/environment';
import { FingerprintService } from '../services/fingerprint.service';


export function initOtelTracing(): void{
  
  const fingerprint = new FingerprintService();

  const collectorUrl = environment.otelCollectorUrl;
  if (!collectorUrl) {
    return;
  }

  const spanProcessors = [
    new BatchSpanProcessor(
      new OTLPTraceExporter(
        {
          url: collectorUrl
        }
      )
    )
  ];

  const provider = new WebTracerProvider(
    {
      resource: defaultResource().merge(
        resourceFromAttributes(
          {
            [ATTR_SERVICE_NAME]: 'mf-unibox',
            'device.fingerprint': fingerprint.getFingerprint(),
          }
        )
      ),
      spanProcessors,
    }
  );

  provider.register(
    {
      contextManager: new ZoneContextManager(),
      propagator: new W3CTraceContextPropagator(),
    }
  );

  registerInstrumentations(
    {
      instrumentations: [
        new UserInteractionInstrumentation(
          {
            eventNames: [
              'click',
              'submit'
            ],
            shouldPreventSpanCreation: (event, element, span) => {
              if (
                event === 'click' && 
                element.tagName === 'BUTTON'
              ) {
                const buttonText = element.innerText?.trim().toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
                const elementId = element.id? `.${ element.id }` : '';
                const spanName = `ui.click.button${ elementId }.${ buttonText }`;
                
                span.updateName(spanName);
                
                span.setAttribute('ui.element.name', buttonText);
                span.setAttribute('ui.element.id', element.id || '');
                span.setAttribute('ui.element.class', element.className || '');
                span.setAttribute('ui.element.text', element.innerText?.slice(0, 50) || '');
                
                // Add click lifecycle events
                span.addEvent(
                  'click.initiated', 
                  {
                    'ui.element.name': buttonText
                  }
                );
                return false;
              }
              
              if (event === 'submit') {
                const formElement = element as HTMLFormElement;
                const formId = formElement.id? `.${ formElement.id }` : '';
                const formClass = formElement.className? `.${ formElement.className.split(' ')[0] }` : '';
                const spanName = `form.submit${ formId }${ formClass }`;
                
                span.updateName(spanName);
                
                span.setAttribute('form.id', formElement.id || '');
                span.setAttribute('form.class', formElement.className || '');
                span.setAttribute('form.action', formElement.action || '');
                span.setAttribute('form.method', formElement.method || 'GET');
                span.setAttribute('form.name', formElement.name || '');
                
                // Count form inputs
                const inputs = formElement.querySelectorAll('input, select, textarea');
                span.setAttribute('form.input_count', inputs.length.toString());
                
                // Add submit lifecycle events
                span.addEvent(
                  'form.submit.initiated', 
                  {
                    'form.id': formElement.id || '',
                    'form.input_count': inputs.length
                  }
                );
                return false;
              }
              return true;
            }
          }
        ),
        
        new FetchInstrumentation(
          {
            propagateTraceHeaderCorsUrls: [
              environment.gqlUrl,
              environment.indiaGqlUrl,
              'http://localhost:4200',
              'http://localhost:5500',
              '/graphql',
            ],
            applyCustomAttributesOnSpan: (span: any, request: Request | RequestInit) => {
              let url = '';
              let method = 'GET';
              
              if (request instanceof Request) {
                url = request.url;
                method = request.method;
              } 
              else if (
                request && 
                typeof request === 'object'
              ) {
                url = (request as any).url || '';
                method = (request as any).method || 'GET';
              }
              configureSpan(span, url, method, 'fetch');
            },
          }
        ),
        
        new XMLHttpRequestInstrumentation(
          {
            propagateTraceHeaderCorsUrls: [
              environment.gqlUrl,
              environment.indiaGqlUrl,
              'http://localhost:4200',
              'http://localhost:5500',
              '/graphql'
            ],
            applyCustomAttributesOnSpan: (span: any, xhr: XMLHttpRequest) => {
              configureSpan(span, xhr.responseURL || '', 'GET', 'xhr');
            },
          }
        ),
      ],
    }
  );
}

export function configureSpan(span: any, url: string, method: string, requestType: 'fetch' | 'xhr'): void{
  if (!url || url.includes('/v1/traces')) {
    return;
  }

  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;
    const search = urlObj.search;
    
    const spanName = `${ method } ${ path }${ search }`;
    span.name = spanName;
    
    span.setAttribute('http.url', url);
    span.setAttribute('http.method', method);
    span.setAttribute('http.host', urlObj.host);
    span.setAttribute('http.server_name', urlObj.hostname);
    span.setAttribute('http.target', `${ path }${ search }`);
    span.setAttribute('http.scheme', urlObj.protocol.replace(':', ''));
    
    span.setAttribute('app.path_only', path);
    span.setAttribute('app.endpoint', spanName);
    span.setAttribute('app.request_type', requestType);
    
    if (path.startsWith('/graphql')) {
      span.setAttribute('app.api_type', 'graphql');
      span.setAttribute('app.service', path.split('/')[2] || 'unknown');
    }
    
  } 
  catch (e) {
    console.error('Failed to parse URL for span configuration', url, e);
    
    const fallbackName = `${method} ${url}`;
    span.name = fallbackName;
    
    span.setAttribute('http.url', url);
    span.setAttribute('http.method', method);
    span.setAttribute('app.endpoint', fallbackName);
    span.setAttribute('app.request_type', requestType);
  }
}
